import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import useSEO from "../lib/useSEO";
import { api } from "../lib/api";
import { Loader2 } from "lucide-react";

export default function AffiliateAuthPage() {
    const { login, user, setSessionFromExchange } = useAuth();
    const navigate = useNavigate();
    const [search, setSearch] = useSearchParams();
    const initialMode = search.get("mode") === "register" ? "register" : "login";
    const [mode, setMode] = useState(initialMode);
    const [form, setForm] = useState({ email: "", password: "", name: "", phone: "", how_youll_promote: "" });
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);

    useSEO({ title: mode === "register" ? "Become a Referral Partner" : "Affiliate Sign In", path: "/affiliate/auth" });

    useEffect(() => {
        if (user?.role === "affiliate") navigate("/affiliate/dashboard", { replace: true });
    }, [user, navigate]);

    const submit = async (e) => {
        e.preventDefault();
        setErr(""); setLoading(true);
        try {
            if (mode === "register") {
                const { data } = await api.post("/affiliate/register", {
                    email: form.email,
                    password: form.password,
                    name: form.name,
                    phone: form.phone || null,
                    how_youll_promote: form.how_youll_promote || null,
                });
                if (setSessionFromExchange) setSessionFromExchange(data);
                else localStorage.setItem("ll_token", data.access_token);
                navigate("/affiliate/dashboard");
            } else {
                await login(form.email, form.password);
                navigate("/affiliate/dashboard");
            }
        } catch (e) {
            setErr(e?.response?.data?.detail || "Couldn't complete that. Please try again.");
        } finally { setLoading(false); }
    };

    return (
        <section className="max-w-md mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
            <h1 className="font-serif text-3xl font-bold mb-2" data-testid="affiliate-auth-headline">
                {mode === "register" ? "Become a referral partner" : "Affiliate sign in"}
            </h1>
            <p className="text-sm text-muted-foreground mb-6">
                {mode === "register"
                    ? "Quick application. You'll get your referral code right after signup."
                    : "Sign in to your referral dashboard."}
            </p>
            <form onSubmit={submit} className="ll-card space-y-3" data-testid="affiliate-auth-form">
                {mode === "register" && (
                    <>
                        <div>
                            <label className="ll-label block mb-1">Full name</label>
                            <input className="ll-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="affiliate-name" />
                        </div>
                        <div>
                            <label className="ll-label block mb-1">Phone (optional)</label>
                            <input className="ll-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="affiliate-phone" />
                        </div>
                    </>
                )}
                <div>
                    <label className="ll-label block mb-1">Email</label>
                    <input className="ll-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="affiliate-email" />
                </div>
                <div>
                    <label className="ll-label block mb-1">Password</label>
                    <input className="ll-input" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="affiliate-password" />
                </div>
                {mode === "register" && (
                    <div>
                        <label className="ll-label block mb-1">How will you promote? (optional)</label>
                        <textarea className="ll-input min-h-[80px]" value={form.how_youll_promote} onChange={(e) => setForm({ ...form, how_youll_promote: e.target.value })} placeholder="Social media, alumni network, podcast audience, etc." data-testid="affiliate-promote" />
                    </div>
                )}
                {err && <div className="text-sm text-destructive" data-testid="affiliate-auth-err">{err}</div>}
                <button type="submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2.5 font-semibold hover:bg-primary/90 disabled:opacity-60" data-testid="affiliate-auth-submit">
                    {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                    {mode === "register" ? "Apply & get my referral code" : "Sign in"}
                </button>
                <p className="text-xs text-center text-muted-foreground pt-2">
                    {mode === "register" ? (
                        <>Already a partner? <button type="button" className="text-primary hover:underline" onClick={() => { setMode("login"); setSearch({ mode: "login" }); }} data-testid="affiliate-switch-login">Sign in</button></>
                    ) : (
                        <>New here? <button type="button" className="text-primary hover:underline" onClick={() => { setMode("register"); setSearch({ mode: "register" }); }} data-testid="affiliate-switch-register">Apply</button> · <Link to="/for-affiliates" className="text-primary hover:underline">Learn about the program</Link></>
                    )}
                </p>
            </form>
        </section>
    );
}
